/**
 * Компонент карточки уведомления
 */

import { memo, type MouseEvent, type ReactNode } from 'react';
import { formatUserName, getDateFnsLocale } from '@/lib';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNow } from 'date-fns';
import { UserAvatar } from '@/components';
import type { Notification } from '../model';
import {
  getNotificationTypeKey,
  getNotificationImage,
  getNotificationTitle,
  isSecretSantaInvite,
  isSecretSantaRelated,
  isCollaboratorInvite,
} from '../lib';

/** Отступ для выравнивания слотов действий с текстом (ширина аватара + gap) */
const ACTIONS_OFFSET_CLASS = 'ml-[60px]';

interface NotificationItemProps {
  notification: Notification;
  onRead: (notificationId: string) => void;
  onClick?: (notification: Notification) => void;
  onNavigateToSecretSanta?: (gameId: string) => void;
  /** Слот для кнопок ответа на приглашение в Тайный Санта */
  secretSantaInviteActionsSlot?: ReactNode;
  /** Слот для кнопок ответа на приглашение в соавторы */
  collaboratorInviteActionsSlot?: ReactNode;
}

export const NotificationItem = memo(function NotificationItem({ 
  notification, 
  onRead, 
  onClick,
  onNavigateToSecretSanta,
  secretSantaInviteActionsSlot,
  collaboratorInviteActionsSlot,
}: NotificationItemProps): JSX.Element {
  const { t } = useTranslation();

  // Форматируем время
  const timeAgo = formatDistanceToNow(notification.createdAt, {
    addSuffix: true,
    locale: getDateFnsLocale(),
  });

  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    // Если это приглашение в Тайный Санта - переходим на страницу игры
    if (
      isSecretSantaInvite(notification) && 
      notification.gameId && 
      onNavigateToSecretSanta
    ) {
      e.stopPropagation();
      if (!notification.isRead) {
        onRead(notification.id);
      }
      onNavigateToSecretSanta(notification.gameId);
      return;
    }

    // Для остальных типов уведомлений о Тайном Санте
    if (
      notification.gameId && 
      onNavigateToSecretSanta &&
      isSecretSantaRelated(notification)
    ) {
      e.stopPropagation();
      if (!notification.isRead) {
        onRead(notification.id);
      }
      onNavigateToSecretSanta(notification.gameId);
      return;
    }

    // Обычное поведение для других уведомлений
    if (!notification.isRead) {
      onRead(notification.id);
    }
    if (onClick) {
      onClick(notification);
    }
  };

  const image = getNotificationImage(notification);
  const title = getNotificationTitle(notification);

  return (
    <div className="flex flex-col">
      <button type="button" className="flex items-center text-left w-full active:scale-[0.98] transition-transform" onClick={handleClick}>
        {/* Аватар пользователя */}
        <UserAvatar
          userId={notification.actor?.id || ''}
          avatarUrl={notification.actor?.avatarUrl}
          displayName={notification.actor?.displayName}
          lastName={notification.actor?.lastName}
          username={notification.actor?.username || ''}
          size="default"
          className="flex-shrink-0"
        />

        {/* Основной контент */}
        <div className="flex-1 min-w-0 ml-3.5">
          <p className="text-base">
            <span className="font-semibold">{formatUserName(notification.actor?.displayName, notification.actor?.lastName, { short: true }) || notification.actor?.username}</span>{' '}
            <span className="text-foreground">{t(getNotificationTypeKey(notification.type))}</span>
          </p>
          {title && (
            <p className="text-sm text-muted-foreground truncate">{title}</p>
          )}
          <p className="text-sm text-muted-foreground">{timeAgo}</p>
        </div>

        {/* Превью изображения (если есть) */}
        {image && (
          <div className="flex-shrink-0">
            <img
              src={image}
              alt={title}
              loading="lazy"
              className="w-12 h-12 rounded-lg object-cover"
            />
          </div>
        )}
      </button>

      {/* Слот для кнопок приглашения в Тайный Санта */}
      {isSecretSantaInvite(notification) && secretSantaInviteActionsSlot && (
        <div className={`mt-3 ${ACTIONS_OFFSET_CLASS}`}>
          {secretSantaInviteActionsSlot}
        </div>
      )}

      {/* Слот для кнопок приглашения в соавторы */}
      {isCollaboratorInvite(notification) && collaboratorInviteActionsSlot && (
        <div className={`mt-3 ${ACTIONS_OFFSET_CLASS}`}>
          {collaboratorInviteActionsSlot}
        </div>
      )}
    </div>
  );
});